import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { isEmpty } from 'lodash';
import moment from 'moment';

import { TextField } from '@material-ui/core';

import './DutyStatusForm.scss';
import { AppButton } from 'src/components/AppButton/AppButton';

interface FormData {
  timestamp: string;
  location: string;
  comment: string;
  longitude: number;
  latitude: number;
  hiddenStatus?: string;
}

interface DutyStatusFormProps {
  onSubmit: (formData: FormData) => void;
  timezone: string;
  lastLogDateTime?: string;
  coordinates: { longitude: number; latitude: number };
}

export const DutyStatusForm: React.FC<DutyStatusFormProps> = ({
  onSubmit,
  timezone,
  lastLogDateTime,
  coordinates,
}) => {
  const coordinateStr = `${coordinates.latitude.toFixed(2)}, ${coordinates.longitude.toFixed(2)}`;
  const { handleSubmit, control, errors } = useForm({
    defaultValues: {
      timestamp: moment()
        .tz(timezone)
        .format('HH:mm'),
      location: coordinateStr,
      comment: '',
    },
  });

  const noErrors = isEmpty(errors);

  const onSubmitHandler = (data: any) => {
    let timestamp;
    moment.fn.toJSON = function() {
      return this.format();
    };
    if (data.timestamp) {
      const currentDate = format(new Date(), 'yyyy-MM-dd');
      timestamp = moment
        .tz(`${currentDate} ${data.timestamp}`, timezone)
        .utc()
        .toJSON();
    }
    onSubmit({
      timestamp: timestamp
        ? timestamp
        : moment()
            .tz(timezone)
            .utc()
            .toJSON(),
      location: data.location,
      comment: data.comment,
      longitude: coordinates.longitude,
      latitude: coordinates.latitude,
    });
  };

  const validateTimeStamp = (value: string): string => {
    // All log times are UTC so we need to convert the selected time to UTC first and then compare
    const splitTime = value.split(':');
    const chosenTime = moment()
      .tz(timezone)
      .set({
        hour: parseInt(splitTime[0]),
        minute: parseInt(splitTime[1]),
      })
      .utc()
      .startOf('minute');
    const isFutureTime = chosenTime.isAfter(
      moment()
        .tz(timezone)
        .utc()
        .startOf('minute'),
    );

    // We should compare the minutes so we ignore seconds and milliseconds
    const lastLogTime = moment(lastLogDateTime).startOf('minute');
    const isPastTime = chosenTime.isSameOrBefore(lastLogTime);

    let validateString = '';
    if (isPastTime) {
      validateString = 'Time can not be early then latest status';
    } else if (isFutureTime) {
      validateString = 'Time can not be in the future';
    }

    return validateString;
  };

  return (
    <div className="ds-form">
      {!noErrors && (
        <div className="ds-form__errors">
          {Object.keys(errors).map(item => (
            <div key={item}>{errors[item].message}</div>
          ))}
        </div>
      )}
      <form noValidate onSubmit={handleSubmit(onSubmitHandler)}>
        <div className="ds-form__control-row">
          <Controller
            name="timestamp"
            error={!!errors.timestamp}
            control={control}
            as={TextField}
            type="time"
            label="Start Time"
            className="ds-form__input-box"
            rules={{
              required: { value: true, message: 'Set the start time.' },
              validate: {
                time: value => validateTimeStamp(value) || true,
              },
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </div>

        <div className="ds-form__control-row ds-form__location-control-row">
          <Controller
            name="location"
            error={!!errors.location}
            as={TextField}
            control={control}
            className="ds-form__input-box"
            label="Coordinates"
            disabled={true}
            InputLabelProps={{
              shrink: true,
            }}
            defaultValue={coordinateStr}
          />
        </div>

        <div className="ds-form__control-row">
          <Controller
            name="comment"
            error={!!errors.comment}
            as={TextField}
            control={control}
            className="ds-form__input-box"
            label="Notes"
            rules={{
              required: { value: true, message: 'Enter a note.' },
            }}
          />
        </div>

        <div className="ds-form__control-submit">
          <AppButton title="Change duty status" type="submit" small />
        </div>
      </form>
    </div>
  );
};
